import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Timer, Square, ChevronDown, ChevronUp, FolderKanban, GripHorizontal } from 'lucide-react';
import api from '../utils/api';
import useDraggable from '../hooks/useDraggable';

const COLLAPSED_KEY = 'project-timer-collapsed';

const GREEN = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_BG = 'rgba(16, 185, 129, 0.12)';
const GREEN_BORDER = 'rgba(16, 185, 129, 0.35)';
const RED = '#ef4444';
const RED_DARK = '#dc2626';

function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function FloatingProjectTimer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');
  const [elapsed, setElapsed] = useState(0);
  const [stopNotes, setStopNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [hoverStop, setHoverStop] = useState(false);

  const { pos: dragPos, isDragging, wasDragged, handlers, elementRef } = useDraggable('project-timer-pos', { defaultZone: 'bottom-left', width: 280, height: 48 });
  const timerRef = useRef(null);

  const { data: activeTimer, isLoading } = useQuery({
    queryKey: ['project-timer-active'],
    queryFn: () => api.get('/projects/timer/active').then(r => r.data),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  useEffect(() => {
    if (activeTimer?.started_at) {
      const calc = () => {
        const start = new Date(activeTimer.started_at).getTime();
        setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
      };
      calc();
      timerRef.current = setInterval(calc, 1000);
      return () => clearInterval(timerRef.current);
    } else {
      setElapsed(0);
    }
  }, [activeTimer]);

  const stopTimer = useMutation({
    mutationFn: () => api.post(`/projects/${activeTimer.project_id}/timer/stop`, { notes: stopNotes || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-timer-active'] });
      queryClient.invalidateQueries({ queryKey: ['project', activeTimer.project_id] });
      setStopNotes('');
      setShowNotes(false);
      toast.success(t('projects.timerStopped'));
    },
    onError: () => toast.error(t('common.actionFailed')),
  });

  useEffect(() => { localStorage.setItem(COLLAPSED_KEY, collapsed); }, [collapsed]);

  if (isLoading || !activeTimer) return null;

  const containerStyle = {
    position: 'fixed',
    left: dragPos.x, top: dragPos.y, right: 'auto', bottom: 'auto',
    zIndex: 9999,
    transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease, box-shadow .15s',
    touchAction: 'none',
    background: 'var(--c-surface)',
    border: `1px solid ${GREEN_BORDER}`,
    borderRadius: 12,
    boxShadow: isDragging
      ? `0 20px 50px rgba(0,0,0,0.45), 0 0 0 3px ${GREEN_BORDER}`
      : '0 12px 32px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)',
    userSelect: 'none',
    minWidth: 260,
    overflow: 'hidden',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    cursor: isDragging ? 'grabbing' : 'grab',
    background: GREEN_BG,
    borderBottom: !collapsed ? `1px solid ${GREEN_BORDER}` : 'none',
  };

  const stopBtnStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: hoverStop ? RED_DARK : RED,
    color: 'white',
    border: 'none',
    padding: '8px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background .12s',
  };

  return (
    <div ref={elementRef} style={containerStyle}>
      {/* Drag handle / header */}
      <div style={headerStyle} {...handlers}>
        <GripHorizontal size={14} style={{ color: GREEN_DARK, opacity: 0.6, flexShrink: 0 }} />
        <Timer size={16} style={{ color: GREEN_DARK, flexShrink: 0, animation: 'pulse 1.6s ease-in-out infinite' }} />
        <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 17, fontWeight: 700, color: GREEN_DARK, letterSpacing: 0.5, fontVariantNumeric: 'tabular-nums' }}>
          {formatElapsed(elapsed)}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { if (!wasDragged.current) setCollapsed(!collapsed); }}
          style={{ background: 'none', border: 'none', color: 'var(--c-text-muted)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
          title={collapsed ? t('common.expand') || 'Expand' : t('common.collapse') || 'Collapse'}
        >
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Expanded content */}
      {!collapsed && (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 340 }}>
          <div
            style={{ cursor: 'pointer', transition: 'color .12s' }}
            onClick={() => navigate(`/projects/${activeTimer.project_id}`)}
            onMouseEnter={e => e.currentTarget.querySelector('.proj-title').style.color = 'var(--c-brand)'}
            onMouseLeave={e => e.currentTarget.querySelector('.proj-title').style.color = 'var(--c-text)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--c-text-subtle)', fontFamily: 'monospace' }}>
              <FolderKanban size={11} /> P-{activeTimer.project_number}
            </div>
            <div className="proj-title" style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)', transition: 'color .12s', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeTimer.project_title}
            </div>
            {activeTimer.task_title && (
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                {activeTimer.task_title}
              </div>
            )}
          </div>

          {showNotes ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                type="text"
                className="form-input"
                style={{ fontSize: 12, padding: '6px 10px' }}
                placeholder={t('projects.timerNotes')}
                value={stopNotes}
                onChange={e => setStopNotes(e.target.value)}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') stopTimer.mutate(); }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => stopTimer.mutate()}
                  disabled={stopTimer.isPending}
                  style={stopBtnStyle}
                  onMouseEnter={() => setHoverStop(true)}
                  onMouseLeave={() => setHoverStop(false)}
                >
                  <Square size={12} /> {t('projects.stopTimer')}
                </button>
                <button
                  onClick={() => setShowNotes(false)}
                  style={{ padding: '6px 10px', fontSize: 12, color: 'var(--c-text-muted)', background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', borderRadius: 8, cursor: 'pointer' }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowNotes(true)}
              style={stopBtnStyle}
              onMouseEnter={() => setHoverStop(true)}
              onMouseLeave={() => setHoverStop(false)}
            >
              <Square size={12} /> {t('projects.stopTimer')}
            </button>
          )}
        </div>
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}
